import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Building2, Loader2, MapPin } from "lucide-react";
import {
  useOfficeBuildings,
  useUpdateInternPlacement,
} from "../../adapters/Requests";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const BuildingAssignmentModal = ({ isOpen, onClose, intern }) => {
  const { data: buildings = [], isLoading } = useOfficeBuildings();
  const updatePlacement = useUpdateInternPlacement();

  const [selectedBuilding, setSelectedBuilding] = useState("");

  useEffect(() => {
    if (intern?.buildingId) {
      setSelectedBuilding(intern.buildingId.toString());
    } else {
      setSelectedBuilding("");
    }
  }, [intern]);

  const relevantBuildings = buildings.filter(
    (b) => b.business_unit?.code === intern?.sbu_csu_code
  );

  const handleSubmit = async () => {
    try {
      await updatePlacement.mutateAsync({
        internId: intern.id,
        office_building_id: selectedBuilding && selectedBuilding !== "none" ? selectedBuilding : null,
      });
      onClose();
    } catch (error) {
      console.error("Failed to update building", error);
    }
  };

  if (!intern) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-900">
            <Building2 className="text-green-600" size={20} />
            Assign Office Location
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="bg-green-50 p-4 rounded-lg border border-green-100 text-sm">
            <div className="flex justify-between mb-1">
              <span className="text-green-600 font-medium">Intern:</span>
              <span className="text-green-900 font-bold">{intern.fullName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-600 font-medium">Region:</span>
              <span className="text-green-900 font-bold flex items-center gap-1">
                <MapPin size={12} /> {intern.companylocation}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Building / Desk
            </label>

            {isLoading ? (
              <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                <Loader2 className="animate-spin w-4 h-4" /> Loading available
                buildings...
              </div>
            ) : relevantBuildings.length === 0 ? (
              <div className="text-center p-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <p className="text-sm font-medium text-gray-600">
                  No buildings found.
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  There are no registered buildings for {intern.businessUnitName || intern.companylocation}
                  .
                </p>
              </div>
            ) : (
              <Select
                value={selectedBuilding}
                onValueChange={(value) => setSelectedBuilding(value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="-- Not Assigned --" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="none">-- Not Assigned --</SelectItem>
                    {relevantBuildings.map((b) => (
                      <SelectItem key={b.id} value={b.id.toString()}>
                        {b.name} {b.address ? `(${b.address})` : ""}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Assigning a building helps track attendance more accurately based
              on geofencing.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={updatePlacement.isPending}
            className="border-gray-300"
          >
            Cancel
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700 text-white"
            onClick={handleSubmit}
            disabled={
              isLoading ||
              updatePlacement.isPending ||
              relevantBuildings.length === 0
            }
          >
            {updatePlacement.isPending ? (
              <Loader2 className="animate-spin w-4 h-4 mr-2" />
            ) : null}
            Save Assignment
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BuildingAssignmentModal;
